import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import Spinner from '@/components/ui/Spinner';
import { db } from '@/lib/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { logger } from '@/lib/logger';
import { useAuth } from '@/contexts/AuthContext';
import { HubSpotService } from '@/modules/QMS/services/hubspotService';
import { GoogleDriveService } from '@/modules/QMS/services/driveService';
import { toast } from 'sonner';

export default function IntegrationImportWizard({ isOpen, onClose, companyId, companyName, onComplete }) {
    const { user } = useAuth();
    const [step, setStep] = useState(0); // 0 = Select Source
    const [integrationSource, setIntegrationSource] = useState(null);
    
    // Step 1: Fetch and Select
    const [isLoadingOwners, setIsLoadingOwners] = useState(false);
    const [owners, setOwners] = useState([]);
    const [selectedOwnerIds, setSelectedOwnerIds] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    // Step 2: Configuration
    // { [ownerId]: { department: 'Sales' | 'PM', team: string, jobTitle: string, role: string, gDriveLink: string } }
    const [userConfigs, setUserConfigs] = useState({});
    
    // G-Drive Auto-Mapping state
    const [gDriveRootUrl, setGDriveRootUrl] = useState('');
    const [isScanningGDrive, setIsScanningGDrive] = useState(false);

    // Step 3: Import
    const [isImporting, setIsImporting] = useState(false);

    useEffect(() => {
        if (isOpen && step === 1 && integrationSource === 'HubSpot') {
            fetchOwners();
        } else if (!isOpen) {
            setStep(0);
            setIntegrationSource(null);
            setOwners([]);
            setSelectedOwnerIds([]);
            setUserConfigs({});
        }
    }, [isOpen, step]);

    const fetchOwners = async () => {
        setIsLoadingOwners(true);
        try {
            const ownersMap = await HubSpotService.fetchOwnersMap();
            const ownersList = Object.entries(ownersMap).map(([id, data]) => ({
                id,
                name: data.name,
                email: data.email || ''
            }));
            setOwners(ownersList);
        } catch (error) {
            logger.error('Failed to fetch HubSpot owners:', error);
        } finally {
            setIsLoadingOwners(false);
        }
    };

    const handleNextToConfig = () => {
        if (selectedOwnerIds.length === 0) {
            alert("Please select at least one user to import.");
            return;
        }
        
        // Initialize configs for selected users
        const newConfigs = { ...userConfigs };
        selectedOwnerIds.forEach(id => {
            if (!newConfigs[id]) {
                newConfigs[id] = {
                    department: 'Sales',
                    team: 'Sales Team 1',
                    jobTitle: '',
                    gDriveLink: '',
                    role: 'User'
                };
            }
        });
        setUserConfigs(newConfigs);
        setStep(2);
    };

    const handleUpdateConfig = (id, field, value) => {
        setUserConfigs(prev => ({
            ...prev,
            [id]: {
                ...prev[id],
                [field]: value
            }
        }));
    };

    const handleScanGDrive = async () => {
        if (!gDriveRootUrl.trim()) return;
        setIsScanningGDrive(true);
        try {
            const rootId = GoogleDriveService.extractFolderId(gDriveRootUrl);
            if (!rootId) throw new Error("Invalid Google Drive URL.");
            const folders = await GoogleDriveService.fetchRepFolders(rootId);
            
            const newConfigs = { ...userConfigs };
            let matchCount = 0;
            const matchedFolderIds = new Set();
            
            folders.forEach(folder => {
                const folderNameNorm = folder.name.toLowerCase().replace(/[^a-z0-9]/g, '');
                
                // Find match in selected owners
                const matchedOwnerId = selectedOwnerIds.find(id => {
                    const owner = owners.find(o => o.id === id);
                    if (!owner) return false;
                    const nameNorm = (owner.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                    return nameNorm && (folderNameNorm.includes(nameNorm) || nameNorm.includes(folderNameNorm));
                });
                
                if (matchedOwnerId && !matchedFolderIds.has(folder.id)) {
                    newConfigs[matchedOwnerId] = {
                        ...newConfigs[matchedOwnerId],
                        gDriveLink: folder.webViewLink || `https://drive.google.com/drive/folders/${folder.id}`
                    };
                    matchCount++;
                    matchedFolderIds.add(folder.id);
                }
            });
            
            setUserConfigs(newConfigs);
            toast.success(`Successfully matched ${matchCount} users to G-Drive folders!`);
        } catch (e) {
            logger.error("G-Drive Scan Failed:", e);
            toast.error("G-Drive Scan Failed: " + e.message);
        } finally {
            setIsScanningGDrive(false);
        }
    };

    const handleImport = async () => {
        if (!companyId) return;
        setIsImporting(true);
        try {
            const batch = [];
            for (const id of selectedOwnerIds) {
                const owner = owners.find(o => o.id === id);
                if (!owner) continue;
                
                const config = userConfigs[id];
                
                const mappedRow = {
                    email: owner.email || `${id}@imported.hubspot`, // fallback if no email
                    primaryName: owner.name,
                    role: config.role,
                    jobTitle: config.jobTitle || 'Team Member',
                    status: 'Active',
                    entitlements: ['crm', 'quality'],
                    hubspotOwnerId: id,
                    googleDriveLink: config.gDriveLink || '',
                    department: config.department,
                    customTeams: config.department === 'Sales' ? [config.team] : ['PM Team'],
                    isManager: config.jobTitle?.toLowerCase().includes('manager') || config.jobTitle?.toLowerCase().includes('president') || config.jobTitle?.toLowerCase().includes('ceo') || config.jobTitle?.toLowerCase().includes('vp') || false,
                    companyId: companyId,
                    company: companyName || 'Unknown Company',
                    createdAt: serverTimestamp(),
                    createdBy: user?.email || 'System Import'
                };

                batch.push(addDoc(collection(db, 'app_users'), mappedRow));
            }

            await Promise.all(batch);
            if (onComplete) onComplete();
            onClose();
        } catch (error) {
            logger.error(error);
            alert("Import failed");
        } finally {
            setIsImporting(false);
        }
    };

    if (!isOpen) return null;

    const steps = [
        { num: 0, label: 'Integration' },
        { num: 1, label: 'Select Users' },
        { num: 2, label: 'Configure Teams' },
        { num: 3, label: 'Import' }
    ];

    const salesTeams = ['Sales Team 1', 'Sales Team 2', 'Sales Team 3', 'Sales Team 4'];

    return (
        <div className="fixed inset-0 bg-[var(--bg-page)] z-[9999] flex flex-col animate-fade-in">
            {/* Header / Stepper */}
            <div className="h-[90px] flex items-center px-8 bg-transparent">
                <div className="w-[250px] text-[15px] font-semibold text-[var(--text-primary)] flex items-center gap-2">
                    <div className="w-7 h-7 rounded-md bg-[var(--bg-muted)] text-[var(--text-primary)] flex items-center justify-center">
                        <ObliqueIcon name="api" size={16} />
                    </div>
                    Import Integration
                </div>
                
                <div className="flex-1 flex justify-center items-center">
                    <div className="flex items-center w-[500px]">
                        {steps.map((s, idx) => {
                            const isDone = step > s.num;
                            const isCurrent = step === s.num;
                            return (
                                <React.Fragment key={s.num}>
                                    <div className="flex flex-col items-center gap-2 relative z-[2]">
                                        <div className={`rounded-full flex items-center justify-center transition-all duration-300 ${
                                            isCurrent ? 'w-5 h-5 border-[3px] border-[#ff7a59] bg-[var(--bg-surface)]' : 
                                            isDone ? 'w-4 h-4 border-none bg-[#ff7a59]' : 
                                            'w-4 h-4 border-2 border-[var(--border)] bg-[var(--bg-surface)]'
                                        }`}>
                                            {isDone && <ObliqueIcon name="check" size={10} color="#fff" weight={700} />}
                                        </div>
                                        <div className={`absolute top-7 whitespace-nowrap text-[13px] font-semibold text-[var(--text-primary)] transition-opacity duration-300 pointer-events-none ${
                                            isCurrent ? 'opacity-100' : 'opacity-0'
                                        }`}>
                                            {s.label}
                                        </div>
                                    </div>
                                    {idx < steps.length - 1 && (
                                        <div className={`flex-1 h-0.5 mx-3 transition-colors duration-300 ${
                                            isDone ? 'bg-[#ff7a59]' : 'bg-[var(--border)]'
                                        }`} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>

                <div className="w-[200px] flex justify-end items-center gap-4">
                    <div className="text-[13px] text-[var(--text-secondary)] font-medium">Step {step} of 3</div>
                    <button onClick={onClose} className="bg-transparent border-0 cursor-pointer text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors">
                        <ObliqueIcon name="close" size={20} />
                    </button>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 overflow-y-auto flex justify-center py-10 px-6">
                
                {/* STEP 0: Select Source */}
                {step === 0 && (
                    <div className="w-full max-w-[800px] flex flex-col">
                        <h1 className="text-2xl font-semibold text-[var(--text-primary)] mb-2">Select Data Source</h1>
                        <p className="text-sm text-[var(--text-secondary)] mb-8">
                            Choose the CRM or Integration you want to extract users from.
                        </p>

                        <div className="grid grid-cols-3 gap-4">
                            <div 
                                onClick={() => { setIntegrationSource('HubSpot'); setStep(1); }}
                                className="p-6 rounded-2xl border border-[var(--border)] bg-[var(--bg-surface)] cursor-pointer transition-all flex flex-col items-center gap-4 hover-bg-subtle"
                            >
                                <div className="w-16 h-16 rounded-2xl bg-[#ff7a5920] border border-[#ff7a5940] flex items-center justify-center">
                                    <img src="https://cdn.svgporn.com/logos/hubspot.svg" width={32} height={32} alt="HubSpot" />
                                </div>
                                <div className="text-base font-semibold text-[var(--text-primary)]">HubSpot</div>
                                <div className="text-xs text-[var(--success)] font-semibold bg-[var(--success-subtle)] px-3 py-1 rounded-full">Connected</div>
                            </div>

                            <div className="p-6 rounded-2xl border border-dashed border-[var(--border)] bg-[var(--bg-page)] opacity-70 flex flex-col items-center gap-4">
                                <div className="w-16 h-16 rounded-2xl bg-[#00aa4f20] border border-[#00aa4f40] flex items-center justify-center">
                                    <img src="https://cdn.svgporn.com/logos/pipedrive.svg" width={32} height={32} alt="Pipedrive" />
                                </div>
                                <div className="text-base font-semibold text-[var(--text-primary)]">Pipedrive</div>
                                <div className="text-xs text-[var(--text-tertiary)] font-medium">Coming Soon</div>
                            </div>

                            <div className="p-6 rounded-2xl border border-dashed border-[var(--border)] bg-[var(--bg-page)] opacity-70 flex flex-col items-center gap-4">
                                <div className="w-16 h-16 rounded-2xl bg-[#00a1e020] border border-[#00a1e040] flex items-center justify-center">
                                    <img src="https://cdn.svgporn.com/logos/salesforce.svg" width={32} height={32} alt="Salesforce" />
                                </div>
                                <div className="text-base font-semibold text-[var(--text-primary)]">Salesforce</div>
                                <div className="text-xs text-[var(--text-tertiary)] font-medium">Coming Soon</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* STEP 1: Select Users */}
                {step === 1 && (
                    <div className="w-full max-w-[800px] flex flex-col">
                        <h1 className="text-2xl font-semibold text-[var(--text-primary)] mb-2">Select HubSpot Users</h1>
                        <p className="text-sm text-[var(--text-secondary)] mb-6">
                            Choose the HubSpot owners you want to import into Oblique.
                        </p>

                        <div className="flex items-center mb-4 bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg px-3">
                            <span className="material-symbols-outlined text-[var(--text-tertiary)] text-xl mr-2">search</span>
                            <input 
                                type="text"
                                placeholder="Search by name or email..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full py-3 border-none bg-transparent outline-none text-sm text-[var(--text-primary)]"
                            />
                        </div>

                        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
                            {isLoadingOwners ? (
                                <div className="p-15 flex justify-center">
                                    <Spinner size={32} />
                                </div>
                            ) : owners.length === 0 ? (
                                <div className="p-10 text-center text-[var(--text-secondary)]">
                                    No HubSpot owners found.
                                </div>
                            ) : (
                                <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                                    <table className="w-full border-collapse">
                                        <thead className="sticky top-0 bg-[var(--bg-muted)] z-10">
                                            <tr>
                                                <th className="px-4 py-3 text-left w-10">
                                                    <input 
                                                        type="checkbox" 
                                                        checked={selectedOwnerIds.length === owners.length && owners.length > 0}
                                                        onChange={(e) => setSelectedOwnerIds(e.target.checked ? owners.map(o => o.id) : [])}
                                                    />
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-secondary)]">Name</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-secondary)]">Email</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {owners
                                                .filter(owner => {
                                                    const q = searchQuery.toLowerCase();
                                                    return (owner.name || '').toLowerCase().includes(q) || (owner.email || '').toLowerCase().includes(q);
                                                })
                                                .map(owner => (
                                                <tr key={owner.id} className="border-t border-[var(--border)]">
                                                    <td className="px-4 py-3">
                                                        <input 
                                                            type="checkbox" 
                                                            checked={selectedOwnerIds.includes(owner.id)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) setSelectedOwnerIds([...selectedOwnerIds, owner.id]);
                                                                else setSelectedOwnerIds(selectedOwnerIds.filter(id => id !== owner.id));
                                                            }}
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3 text-[13px] font-medium text-[var(--text-primary)]">{owner.name}</td>
                                                    <td className="px-4 py-3 text-[13px] text-[var(--text-secondary)]">{owner.email || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center mt-6 relative">
                            <button onClick={() => setStep(0)} className="btn btn-secondary">
                                Back
                            </button>
                            <div className="absolute left-1/2 -translate-x-1/2 text-[13px] text-[var(--text-secondary)]">
                                {selectedOwnerIds.length} selected
                            </div>
                            <button onClick={handleNextToConfig} className="btn btn-primary ml-auto" disabled={selectedOwnerIds.length === 0}>
                                Continue
                            </button>
                        </div>
                    </div>
                )}

                {/* STEP 2: Configure Teams */}
                {step === 2 && (
                    <div className="w-full max-w-[900px] flex flex-col">
                        <h1 className="text-2xl font-semibold text-[var(--text-primary)] mb-2">Configure Departments & Teams</h1>
                        <p className="text-sm text-[var(--text-secondary)] mb-6">
                            Assign the imported users to PM or Sales teams, and designate managers.
                        </p>

                        <div className="bg-[var(--bg-subtle)] p-4 rounded-xl border border-[var(--border)] mb-6 flex gap-4 items-center">
                            <div className="w-10 h-10 rounded-lg bg-white border border-[var(--border)] flex items-center justify-center shrink-0">
                                <img src="https://cdn.svgporn.com/logos/google-drive.svg" width={24} height={24} alt="Drive" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[13px] font-semibold text-[var(--text-primary)] mb-1">Auto-Map G-Drive Folders</div>
                                <input 
                                    type="text" 
                                    value={gDriveRootUrl} 
                                    onChange={e => setGDriveRootUrl(e.target.value)} 
                                    placeholder="Paste Root G-Drive Folder URL (e.g. Sales Meetings folder)"
                                    className="w-full px-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] text-xs outline-none text-[var(--text-primary)]"
                                />
                            </div>
                            <button 
                                onClick={handleScanGDrive} 
                                disabled={isScanningGDrive || !gDriveRootUrl.trim()} 
                                className="btn btn-primary bg-blue-500 hover:bg-blue-600 border-none text-white self-end h-[34px] px-4 text-xs shrink-0"
                            >
                                {isScanningGDrive ? <Spinner size={14} color="#fff" /> : 'Scan & Match'}
                            </button>
                        </div>

                        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
                            <div className="max-h-[500px] overflow-y-auto custom-scrollbar">
                                <table className="w-full border-collapse">
                                    <thead className="sticky top-0 bg-[var(--bg-muted)] z-10">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-secondary)]">User</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-secondary)]">Department</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-secondary)]">Team (If Sales)</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-secondary)]">Job Title / Role</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-secondary)]">G-Drive Link</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedOwnerIds.map(id => {
                                            const owner = owners.find(o => o.id === id);
                                            const config = userConfigs[id] || {};
                                            return (
                                                <tr key={id} className="border-t border-[var(--border)]">
                                                    <td className="px-4 py-3">
                                                        <div className="text-[13px] font-semibold text-[var(--text-primary)]">{owner?.name}</div>
                                                        <div className="text-xs text-[var(--text-secondary)]">{owner?.email}</div>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <select 
                                                            value={config.department} 
                                                            onChange={e => handleUpdateConfig(id, 'department', e.target.value)}
                                                            className="w-full px-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)] text-[13px] outline-none"
                                                        >
                                                            <option value="Sales">Sales</option>
                                                            <option value="PM">PM (Project Management)</option>
                                                        </select>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        {config.department === 'Sales' ? (
                                                            <select 
                                                                value={config.team} 
                                                                onChange={e => handleUpdateConfig(id, 'team', e.target.value)}
                                                                className="w-full px-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)] text-[13px] outline-none"
                                                            >
                                                                {salesTeams.map(t => <option key={t} value={t}>{t}</option>)}
                                                            </select>
                                                        ) : (
                                                            <div className="text-[13px] text-[var(--text-tertiary)] px-2">PM Team (Default)</div>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 w-[220px]">
                                                        <input 
                                                            type="text" 
                                                            value={config.jobTitle || ''}
                                                            onChange={e => handleUpdateConfig(id, 'jobTitle', e.target.value)}
                                                            placeholder="e.g. Vice President, CEO"
                                                            className="w-full min-w-[180px] px-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)] text-[13px] outline-none"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3 w-[250px]">
                                                        <input 
                                                            type="text" 
                                                            value={config.gDriveLink || ''}
                                                            onChange={e => handleUpdateConfig(id, 'gDriveLink', e.target.value)}
                                                            placeholder="Google Drive URL"
                                                            className="w-full min-w-[220px] px-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)] text-[13px] outline-none"
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="flex justify-between items-center mt-6">
                            <button onClick={() => setStep(1)} className="btn btn-secondary">
                                Back
                            </button>
                            <button onClick={() => setStep(3)} className="btn btn-primary">
                                Finalize Import
                            </button>
                        </div>
                    </div>
                )}

                {/* STEP 3: Import Status */}
                {step === 3 && (
                    <div className="w-full max-w-[500px] flex flex-col items-center text-center">
                        <div className="w-16 h-16 rounded-2xl bg-[#ff7a5920] text-[#ff7a59] flex items-center justify-center mb-6">
                            <ObliqueIcon name="group_add" size={32} />
                        </div>
                        <h1 className="text-2xl font-semibold text-[var(--text-primary)] mb-2">Ready to Import</h1>
                        <p className="text-sm text-[var(--text-secondary)] mb-8 leading-relaxed">
                            You are about to import <strong>{selectedOwnerIds.length}</strong> users from HubSpot. They will be immediately available in the workspace.
                        </p>

                        <div className="flex gap-4">
                            <button onClick={() => setStep(2)} className="btn btn-secondary" disabled={isImporting}>
                                Back
                            </button>
                            <button onClick={handleImport} className="btn btn-primary px-6 py-2.5" disabled={isImporting}>
                                {isImporting ? <Spinner size={16} color="#fff" /> : 'Import Users Now'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
